import "common/Charts/chartRegistry";
import {
  CSSProperties,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import LoadingSpinnerOverlay, {
  LoadingSpinnerOverlayProps,
} from "common/Overlays/LoadingSpinnerOverlay";
import { BTC } from "common/Icons/Icons";
import { renderToString } from "react-dom/server.browser";
import {
  getTooltipGenerator,
  lineChartTooltipInnerHtmlGenerator,
} from "common/Charts/tooltipGenerator.jsx";
import {
  getTimeChartOptions,
  lineChartHoverPlugin,
  yAxisTagsPlugin,
} from "utils/charts.js";
import { Chart } from "chart.js";
import type { ChartData, ChartDataset, ChartOptions } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Line } from "react-chartjs-2";
import { DISPLAY_CURRENCIES } from "common/constants";
import styled, { useTheme } from "styled-components";
import { v4 as uuid } from "uuid";
import useMeasure from "utils/hooks/useMeasure";
import { hexToRGB } from "@shared/utils/misc";
import { showListQueryEmptyState } from "@shared/common/utils";
import ChartEmpty from "common/Charts/ChartEmpty";
import {
  DisplayCurrency,
  FormattedChartDataPoint,
  LineAnnotation,
  MultiDataset,
  PointAnnotation,
} from "@shared/types";
import Flex from "common/Boxes/Flex";
import EyeInvisibleOutlined from "@ant-design/icons/EyeInvisibleOutlined";
import EyeOutlined from "@ant-design/icons/EyeOutlined";
import { toggleArrayItem } from "@shared/utils/arrays";
import Text from "common/Typography/Text";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import { LINE_CHART_ID } from "testIds.ts";

interface LineChartProps {
  data?: FormattedChartDataPoint[];
  multiData?: MultiDataset[];
  loading?: boolean;
  loadingProps?: LoadingSpinnerOverlayProps;
  displayCurrency?: DisplayCurrency;
  annotations?: (LineAnnotation | PointAnnotation)[];
  precision?: number;
  extendToAnnotations?: boolean;
  isMinified?: boolean;
  style?: CSSProperties;
  color?: string;
  numOfXTicks?: number;
  height?: number;
  isSuccess?: boolean;
  isSmallChart?: boolean;
  isWebsocket?: boolean;
  xMin?: string;
  xMax?: string;
  valueSuffix?: string;
  extraLegendButton?: ReactNode;
  isSmallScreen?: boolean;
  hideXAxis?: boolean;
  hideYAxis?: boolean;
  ticksColor?: string;
  numOfYTicks?: number;
  hideGradientBg?: boolean;
  lastDataPointLabel?: string;
  hideTooltip?: boolean;
  axisOverlapData?: boolean;
  gridColor?: string;
  lineWidth?: number;
  includeBounds?: boolean;
  addPadding?: boolean;
}

const LineChart = ({
  data,
  multiData,
  loading,
  loadingProps,
  displayCurrency,
  annotations,
  precision,
  extendToAnnotations,
  isMinified,
  style,
  color,
  numOfXTicks,
  height: propsHeight,
  isSuccess,
  isSmallChart,
  isWebsocket,
  xMin,
  xMax,
  valueSuffix,
  extraLegendButton,
  isSmallScreen,
  hideXAxis,
  hideYAxis,
  ticksColor,
  numOfYTicks,
  hideGradientBg,
  lastDataPointLabel,
  hideTooltip,
  axisOverlapData,
  gridColor,
  lineWidth,
  includeBounds,
  addPadding,
}: LineChartProps) => {
  const { isDrawer } = useContext(DrawerContext) || {};
  const [visibleDatasets, setVisibleDatasets] = useState<string[]>();
  const [chartContext, setChartContext] = useState<CanvasRenderingContext2D>();
  const chartUuid = useRef(uuid()).current;
  const [measureRef, { width, height: measureHeight }] =
    useMeasure<HTMLDivElement>();
  const [multiDataLegendRef, { height: multiDataLegendHeight }] =
    useMeasure<HTMLDivElement>();
  const height = propsHeight ?? measureHeight;
  const appTheme = useTheme();
  const currencyIcon = useMemo(() => {
    if (!displayCurrency) return "";
    return displayCurrency === DISPLAY_CURRENCIES.BTC
      ? renderToString(<BTC />)
      : "$";
  }, [displayCurrency]);
  const defaultChartColors = useMemo(
    () => [color || appTheme.colors.primaryBlue, appTheme.colors.yellow],
    [appTheme.colors.primaryBlue, appTheme.colors.yellow, color]
  );

  useEffect(() => {
    if (!visibleDatasets && multiData) {
      setVisibleDatasets(multiData.map((dataset) => dataset.name));
    }
  }, [multiData, visibleDatasets]);

  const filteredMultiData: (MultiDataset & { index: number })[] | undefined =
    useMemo(() => {
      return multiData
        ?.map((dataset, index) => ({ ...dataset, index }))
        .filter((dataset) => visibleDatasets?.includes(dataset.name));
    }, [multiData, visibleDatasets]);

  const setChartReference = useCallback(
    (
      chart: Chart<"line", FormattedChartDataPoint[]> | null | undefined
    ): void => {
      if (chart) {
        setChartContext((currentContext) =>
          currentContext === chart.ctx ? currentContext : chart.ctx
        );
      }
    },
    []
  );

  const shouldFillGradient = !multiData || multiData.length === 1;
  const getGradient = useCallback(
    (
      context: CanvasRenderingContext2D | undefined,
      color: string,
      isBelow?: boolean
    ): CanvasGradient | "transparent" => {
      if (!context || !shouldFillGradient) return "transparent";
      const gradient = context.createLinearGradient(
        0,
        isBelow ? height || 0 : 0,
        0,
        isBelow ? 0 : height || 0
      );
      gradient.addColorStop(0, hexToRGB(color, 0.18) ?? color);
      gradient.addColorStop(0.9, hexToRGB(color, 0.01) ?? color);
      return gradient;
    },
    [height, shouldFillGradient]
  );

  const getDataset = useCallback(
    (
      context: CanvasRenderingContext2D | undefined,
      dataPoints: FormattedChartDataPoint[],
      label: string,
      color: string,
      hideBg?: boolean
    ): ChartDataset<"line", FormattedChartDataPoint[]> => ({
      label,
      data: dataPoints,
      fill: {
        target: "origin",
        above: hideBg ? "transparent" : getGradient(context, color),
        below: hideBg ? "transparent" : getGradient(context, color, true),
      },
      borderColor: color,
      borderWidth: lineWidth ?? (isMinified ? 1.5 : 3),
      pointHoverRadius: 6,
      pointHoverBorderWidth: 3,
      hoverBackgroundColor: appTheme.colors.cardBgColor,
      parsing: isMinified ? false : { xAxisKey: "x", yAxisKey: "y" },
      // New look: monotone curves (no overshoot, no corners); classic keeps
      // its tension.
      ...(appTheme.isNewLook
        ? { cubicInterpolationMode: "monotone" as const }
        : { tension: 0.2 }),
      pointRadius: 0,
    }),
    [
      appTheme.colors.cardBgColor,
      appTheme.isNewLook,
      getGradient,
      isMinified,
      lineWidth,
    ]
  );

  const getDatasets = useCallback(
    (
      context: CanvasRenderingContext2D | undefined
    ): ChartDataset<"line", FormattedChartDataPoint[]>[] => {
      if (data) {
        return [
          getDataset(
            context,
            data,
            "First dataset",
            defaultChartColors[0],
            hideGradientBg
          ),
        ];
      }
      return (filteredMultiData || []).map((datasetData) =>
        getDataset(
          context,
          datasetData.data,
          datasetData.name,
          datasetData.color ?? defaultChartColors[datasetData.index],
          hideGradientBg
        )
      );
    },
    [data, defaultChartColors, filteredMultiData, getDataset, hideGradientBg]
  );

  const chartData = useMemo<ChartData<"line", FormattedChartDataPoint[]>>(
    () => ({ datasets: getDatasets(chartContext) }),
    [chartContext, getDatasets]
  );

  const chartParentElementId = chartUuid + "_chart_parent";
  const hasMultipleDatasets = !!multiData && multiData.length > 1;
  const tooltipGenerator = useMemo(() => {
    if (isMinified || hideTooltip) return false;
    return getTooltipGenerator(
      chartUuid + "_tooltip",
      lineChartTooltipInnerHtmlGenerator(currencyIcon, hasMultipleDatasets),
      false,
      null,
      chartParentElementId
    );
  }, [
    chartParentElementId,
    chartUuid,
    currencyIcon,
    hasMultipleDatasets,
    hideTooltip,
    isMinified,
  ]);

  const plugins = useMemo(
    () =>
      isMinified
        ? []
        : [lineChartHoverPlugin, yAxisTagsPlugin, ChartDataLabels],
    [isMinified]
  );

  const showEmptyState =
    !xMax && showListQueryEmptyState(data, loading, isSuccess);

  const multiDataLegend = useMemo(() => {
    if (!multiData) return;
    return (
      <Flex
        $flexDirection={isSmallScreen ? "column" : "row"}
        $alignItems={isSmallScreen ? "flex-start" : "center"}
        ref={multiDataLegendRef}
      >
        {extraLegendButton}
        <LegendWrapper>
          {multiData.map((dataset, index) => {
            const name = dataset.name;
            return (
              <LegendItemWrapper
                onClick={() =>
                  setVisibleDatasets(toggleArrayItem(visibleDatasets, name))
                }
                key={name}
              >
                <Circle $color={dataset.color ?? defaultChartColors[index]} />
                <StyledText as="div">
                  {name}
                  <span style={{ marginLeft: 10 }}>
                    {visibleDatasets?.includes(name) ? (
                      <EyeOutlined />
                    ) : (
                      <EyeInvisibleOutlined />
                    )}
                  </span>
                </StyledText>
              </LegendItemWrapper>
            );
          })}
        </LegendWrapper>
      </Flex>
    );
  }, [
    defaultChartColors,
    extraLegendButton,
    isSmallScreen,
    multiData,
    multiDataLegendRef,
    visibleDatasets,
  ]);

  const updatedAnnotations = useMemo(() => {
    return annotations?.map((annotation) => {
      if (annotation.type === "line") {
        return { ...annotation, textColor: appTheme.colors.layoutBgColor };
      }
      return annotation;
    });
  }, [annotations, appTheme.colors.layoutBgColor]);

  const chartOptions = useMemo(
    () =>
      getTimeChartOptions(
        tooltipGenerator,
        precision,
        updatedAnnotations,
        extendToAnnotations,
        data,
        multiData,
        isMinified,
        appTheme,
        height,
        numOfXTicks,
        false,
        isWebsocket,
        xMin,
        xMax,
        valueSuffix,
        hideXAxis,
        hideYAxis,
        ticksColor,
        numOfYTicks,
        lastDataPointLabel,
        hideTooltip,
        axisOverlapData,
        gridColor,
        includeBounds,
        addPadding
      ) as ChartOptions<"line">,
    [
      addPadding,
      appTheme,
      axisOverlapData,
      data,
      extendToAnnotations,
      gridColor,
      height,
      hideTooltip,
      hideXAxis,
      hideYAxis,
      includeBounds,
      isMinified,
      isWebsocket,
      lastDataPointLabel,
      multiData,
      numOfXTicks,
      numOfYTicks,
      precision,
      ticksColor,
      tooltipGenerator,
      updatedAnnotations,
      valueSuffix,
      xMax,
      xMin,
    ]
  );

  const chart = showEmptyState ? (
    <ChartEmpty isSmallChart={isSmallChart} />
  ) : (
    <Line<FormattedChartDataPoint[]>
      ref={setChartReference}
      style={{ width: "100%" }}
      data={chartData}
      options={chartOptions}
      plugins={plugins}
    />
  );

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        position: "relative",
        ...style,
      }}
      ref={measureRef}
      id={chartParentElementId}
      data-testid={LINE_CHART_ID}
    >
      <LoadingSpinnerOverlay
        isLoading={loading}
        overlayOpacity={0.7}
        wrapStyle={{ height: "100%" }}
        backgroundColor={
          appTheme.colors[isDrawer ? "layoutBgColor" : "cardBgColor"]
        }
        {...loadingProps}
      >
        {multiDataLegend}
        {!!height && !!width && (
          <div style={{ height: height - multiDataLegendHeight, width }}>
            {chart}
          </div>
        )}
      </LoadingSpinnerOverlay>
    </div>
  );
};

export default LineChart;

Chart.register(annotationPlugin);

const LegendWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 12px;
  flex: 1;
  align-self: flex-end;
`;
const LegendItemWrapper = styled(Flex)`
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background-color 0.25s;
  &:hover {
    background-color: ${(p) => p.theme.colors.statisticsBgColor};
  }
`;
const Circle = styled.div<{ $color: string }>`
  height: 12px;
  width: 12px;
  border-radius: 50%;
  background-color: ${(p) => p.$color};
  margin-right: 6px;
`;
const StyledText = styled(Text)<{ $isSmallLegend?: boolean }>`
  ${(p) =>
    p.$isSmallLegend &&
    `
    font-size: ${p.theme.typography.fontSizes.S};
  `};
`;
